import { ColDef, ColGroupDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { GridLoadingIndicator } from "components/widgets";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CiqLogPageHeader from "components/ciq-log-page-header";
import {
  MUTATION_UPDATE_PROJECT_FEATURE_FLAG,
  MUTATION_UPDATE_SUBSCRIPTION_FEATURE_FLAG,
  MUTATION_UPDATE_SUBSCRIPTION_FUTURE_PROJECTS_FLAG
} from "services/graphQL/mutations";
import { useMutation } from "@apollo/client";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { useFeatureFlags } from "../organization-details/use-feature-flags";
import {
  ProjectSwitchCellRenderer,
  SubscriptionSwitchCellRenderer,
  FutureProjectsHeaderToggle,
  ColumnGroupSearchHeader,
  matchProjectFeatures
} from "./cell-renderers";

const headerTooltipMap: any = {
  MATERIAL_EXTRACTION:
    "Shows Extracted Tab in Materials and 'Extract from Drawings' button.",
  SMART_LINKING:
    "Onboarding Automation - Smart suggestions of Activities in Material linking page. Enabling this for a project will also enable Onboarding Automation in Create Project for all new projects to be created in the Subscription.",
  PROCUREMENT_SCHEDULE: "Timeline in Project Dashboard.",
  REQUEST_LEAD_TIME: "Send request to SCs from Material Log",
  BUYOUT: "Bid & Design Package",
  UPCOMING_MILESTONES_NOTIFICATION: "Procurement Follow-up Report",
  MATERIAL_PROGRESS_DASHBOARD: "Appears in Analytics tab of Project Dashboard.",
  MATERIAL_READINESS_DASHBOARD:
    "Appears in Analytics tab of Project Dashboard.",
  CUSTOM_REPORT: "Custom Material Report.",
  SUBMITTAL_PROGRESS_DASHBOARD:
    "Appears in Analytics tab of Project Dashboard.",
  DESIGN_REVIEW_QUEUE_FORECAST_DASHBOARD:
    "Appears in Analytics tab of Project Dashboard.",
  USAGE_SUMMARY_DASHBOARD: "Appears in Analytics tab at Subscription Level",
  EXECUTIVE_OVERVIEW_DASHBOARD: "Appears in Analytics tab at Subscription Level"
};

const toTitleCase = (text: string) =>
  text
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// Group header holds the title + search input; AG Grid sizes groups from
// visible children only, so keep remaining leaves wide enough for that UI.
const FEATURE_GROUP_MIN_WIDTH = 450;
const DEFAULT_FEATURE_COL_WIDTH = 150;

// Build a feature -> { id, enabled } map of the subscription-level
// "enable for future projects" entitlements.
const buildFutureProjectsMap = (entitlements: any[] | undefined) => {
  const map: Record<string, { id: number; enabled: boolean }> = {};
  entitlements?.forEach((entitlement: any) => {
    const feature = entitlement.product_entitlement?.feature;
    const id = entitlement.product_entitlement?.id;
    if (feature && id != null) {
      map[feature] = { id, enabled: !!entitlement.enable_for_future_projects };
    }
  });
  return map;
};

// Detail cell renderer for projects under each subscription
function ProjectDetailCellRenderer({ data }: any) {
  const [detailData, setDetailData] = useState<any[]>([]);
  const detailGridRef = useRef<AgGridReact>(null);

  const [updateProjectFeatureFlagMutation] = useMutation(
    MUTATION_UPDATE_PROJECT_FEATURE_FLAG
  );

  const [updateSubscriptionFutureProjectsMutation] = useMutation(
    MUTATION_UPDATE_SUBSCRIPTION_FUTURE_PROJECTS_FLAG
  );

  // Per-feature "enable for future projects" state, seeded from the query and
  // updated on toggle. Kept in component state (not a memo over the query data)
  // so the header re-seeds from the latest value rather than the stale initial
  // one when ag-grid re-renders/recreates the header cell.
  const [futureProjectsByFeature, setFutureProjectsByFeature] = useState<
    Record<string, { id: number; enabled: boolean }>
  >(() => buildFutureProjectsMap(data?.future_projects_entitlements));
  const [featureColumnSearch, setFeatureColumnSearch] = useState("");
  const featureColumnSearchRef = useRef(featureColumnSearch);
  featureColumnSearchRef.current = featureColumnSearch;
  const [projectRowSearch, setProjectRowSearch] = useState("");
  const projectRowSearchRef = useRef(projectRowSearch);
  projectRowSearchRef.current = projectRowSearch;

  useEffect(() => {
    setFutureProjectsByFeature(
      buildFutureProjectsMap(data?.future_projects_entitlements)
    );
  }, [data?.future_projects_entitlements]);

  // Handler for updating the subscription-level "enable for future projects"
  // flag for a single feature.
  const updateSubscriptionFutureProjects = useCallback(
    async (variables: {
      subscriptionId: string;
      featureFlagId: number;
      enableForFutureProjects: boolean;
    }) => {
      try {
        const result = await updateSubscriptionFutureProjectsMutation({
          variables
        });
        if (result?.data?.update_feature_flag?.message) {
          // Update local state so the toggle reflects the new value and survives
          // header re-renders.
          setFutureProjectsByFeature((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((feature) => {
              if (next[feature].id === variables.featureFlagId) {
                next[feature] = {
                  ...next[feature],
                  enabled: variables.enableForFutureProjects
                };
              }
            });
            return next;
          });
          // Persist into the master row data too, so the value also survives the
          // detail grid being collapsed and re-expanded (the master query is
          // no-cache, so there is nothing else to re-seed it from).
          const entry = data?.future_projects_entitlements?.find(
            (e: any) => e.product_entitlement?.id === variables.featureFlagId
          );
          if (entry) {
            entry.enable_for_future_projects =
              variables.enableForFutureProjects;
          }
        }
        return result;
      } catch (err) {
        console.error("Failed to update enable for future projects flag:", err);
        throw err;
      }
    },
    [
      updateSubscriptionFutureProjectsMutation,
      data?.future_projects_entitlements
    ]
  );

  // Extract unique project-level features
  const projectFeatures = useMemo(() => {
    if (!data?.projects) return [];

    const featuresSet = new Set<string>();
    data.projects.forEach((project: any) => {
      project.project_product_entitlements?.forEach((entitlement: any) => {
        if (entitlement.product_entitlement?.feature) {
          featuresSet.add(entitlement.product_entitlement.feature);
        }
      });
    });

    return Array.from(featuresSet).sort((a, b) => a.localeCompare(b));
  }, [data?.projects]);

  const getFeatureColumnSearch = useCallback(
    () => featureColumnSearchRef.current,
    []
  );

  const getProjectRowSearch = useCallback(
    () => projectRowSearchRef.current,
    []
  );

  const onProjectRowSearch = useCallback((value: string) => {
    setProjectRowSearch(value);
  }, []);

  const applyFeatureColumnVisibility = useCallback(
    (search: string) => {
      const gridApi = detailGridRef.current?.api;
      if (!gridApi || projectFeatures.length === 0) return;

      const query = search.trim();
      const matchingFeatures = query
        ? matchProjectFeatures(search, projectFeatures)
        : projectFeatures;
      // Keep every column visible when nothing matches so the group header
      // (and this search input) does not disappear.
      const visibleFeatures =
        matchingFeatures.length > 0 ? matchingFeatures : projectFeatures;
      const visibleSet = new Set(visibleFeatures);

      const toShow: string[] = [];
      const toHide: string[] = [];
      projectFeatures.forEach((feature) => {
        const colId = `${feature}.enabled`;
        if (visibleSet.has(feature)) toShow.push(colId);
        else toHide.push(colId);
      });

      if (toShow.length) gridApi.setColumnsVisible?.(toShow, true);
      if (toHide.length) gridApi.setColumnsVisible?.(toHide, false);

      // Expand remaining leaves so the group header cannot collapse below the
      // space needed for "Project Level Features" + the search input.
      const widthPerVisible = Math.max(
        DEFAULT_FEATURE_COL_WIDTH,
        Math.ceil(FEATURE_GROUP_MIN_WIDTH / visibleFeatures.length)
      );
      gridApi.setColumnWidths?.(
        visibleFeatures.map((feature) => ({
          key: `${feature}.enabled`,
          newWidth: widthPerVisible
        }))
      );
    },
    [projectFeatures]
  );

  const onFeatureColumnSearch = useCallback(
    (value: string) => {
      setFeatureColumnSearch(value);
      applyFeatureColumnVisibility(value);
    },
    [applyFeatureColumnVisibility]
  );

  useEffect(() => {
    applyFeatureColumnVisibility(featureColumnSearch);
  }, [applyFeatureColumnVisibility, featureColumnSearch]);

  const defaultColDef = useMemo(() => {
    return {
      sortable: false,
      editable: false,
      draggable: false,
      filter: false,
      resizable: true,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  // Generate dynamic column definitions for project features
  const detailColumnDefs: (ColDef | ColGroupDef)[] = useMemo(() => {
    const dynamicFeatureColumns: ColGroupDef[] = projectFeatures.map(
      (feature) => ({
        headerName: feature?.replaceAll("_", " "),
        headerTooltip: headerTooltipMap[feature] ?? toTitleCase(feature),
        minWidth: 250,
        draggable: false,
        children: [
          {
            colId: `${feature}.enabled`,
            field: `${feature}.enabled`,
            // The header itself is the subscription-level "enable for future
            // projects" toggle; the body cells stay as per-project switches.
            headerComponent: FutureProjectsHeaderToggle,
            headerComponentParams: {
              subscriptionId: data?.id,
              featureFlagId: futureProjectsByFeature[feature]?.id,
              initialValue: futureProjectsByFeature[feature]?.enabled ?? false,
              onToggle: updateSubscriptionFutureProjects
            },
            minWidth: 150,
            cellRenderer: ProjectSwitchCellRenderer,
            // Feature cells are objects; keep Quick Filter scoped to project name.
            getQuickFilterText: () => ""
          }
        ]
      })
    );

    return [
      {
        // Nested so the leaf header ("Enable for Future/New Project") lines up
        // with the per-feature toggle row, which sits at the deepest header
        // level. "Project" stays on the row above it.
        headerName: "",
        children: [
          {
            headerName: "Project",
            minWidth: 280,
            headerGroupComponentParams: {
              innerHeaderGroupComponent: ColumnGroupSearchHeader,
              getSearchValue: getProjectRowSearch,
              onSearchChange: onProjectRowSearch,
              placeholder: "Search projects",
              searchAriaLabel: "Search projects"
            },
            children: [
              {
                field: "name",
                headerName: "Enable for Future/New Project",
                headerTooltip: "Enable for Future/New Project",
                minWidth: 280,
                cellRenderer: "agGroupCellRenderer",
                getQuickFilterText: (params) => params.value,
                pinned: "left",
                lockPosition: "left",
                draggable: false
              }
            ]
          }
        ]
      },
      {
        headerName: "Project Level Features",
        minWidth: 400,
        draggable: false,
        headerGroupComponentParams: {
          innerHeaderGroupComponent: ColumnGroupSearchHeader,
          getSearchValue: getFeatureColumnSearch,
          onSearchChange: onFeatureColumnSearch,
          placeholder: "Search features",
          searchAriaLabel: "Search project level features",
          matchItems: projectFeatures
        },
        children: dynamicFeatureColumns
      } as ColGroupDef
    ];
  }, [
    projectFeatures,
    futureProjectsByFeature,
    data?.id,
    updateSubscriptionFutureProjects,
    getFeatureColumnSearch,
    onFeatureColumnSearch,
    getProjectRowSearch,
    onProjectRowSearch
  ]);

  useEffect(() => {
    // Map projects data with flattened entitlements
    if (data?.projects) {
      const transformedProjects = data.projects.map((project: any) => {
        const rowData: any = {
          id: project.id,
          name: project.name,
          project_product_entitlements: project.project_product_entitlements
        };

        // Flatten entitlements into direct properties
        project.project_product_entitlements?.forEach((entitlement: any) => {
          const feature = entitlement.product_entitlement?.feature;
          if (feature) {
            rowData[feature] = {
              enabled: entitlement.enabled,
              enableForNewProjects: entitlement.enable_for_new_project
            };
          }
        });

        return rowData;
      });

      setDetailData(transformedProjects);
    }
  }, [data?.projects]);

  // Handler for updating project entitlements
  const updateProjectFeatureFlag = async (variables: {
    projectId: string;
    featureFlagId: number;
    featureFlag: boolean;
  }) => {
    try {
      const result = await updateProjectFeatureFlagMutation({ variables });
      return result;
    } catch (err) {
      console.error("Failed to update project feature flag:", err);
      throw err;
    }
  };

  return (
    <div
      className="ag-theme-alpine py-2 px-16"
      style={{
        // paddingLeft: "5%",
        // paddingRight: "5%",
        // width: "100%",
        height: "350px"
      }}
    >
      <AgGridReact
        ref={detailGridRef}
        defaultColDef={defaultColDef}
        columnDefs={detailColumnDefs}
        rowData={detailData}
        headerHeight={42}
        rowHeight={42}
        quickFilterText={projectRowSearch}
        tooltipShowMode="standard"
        onGridReady={() => {
          applyFeatureColumnVisibility(featureColumnSearchRef.current);
        }}
        context={{
          updateProjectFeatureFlag
        }}
      />
    </div>
  );
}

interface FeatureFlagSettingsProps {
  organization_id: string;
}

function FeatureFlagSettings({ organization_id }: FeatureFlagSettingsProps) {
  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  // Fetch feature flags data using the custom hook
  const {
    data: subscriptionData,
    loading,
    error
  } = useFeatureFlags({
    organizationId: organization_id
  });

  // Mutations for updating entitlements

  const [updateSubscriptionFeatureFlagMutation] = useMutation(
    MUTATION_UPDATE_SUBSCRIPTION_FEATURE_FLAG
  );

  const updateSubscriptionFeatureFlag = async (variables: {
    subscriptionId: string;
    featureFlagId: number;
    featureFlag: boolean;
  }) => {
    try {
      const result = await updateSubscriptionFeatureFlagMutation({ variables });
      return result;
    } catch (err) {
      console.error("Failed to update subscription feature flag:", err);
      throw err;
    }
  };

  // Extract all unique features from subscription_product_entitlements
  const allUniqueFeatures = useMemo(() => {
    if (!subscriptionData?.subscription) return [];

    const featuresSet = new Set<string>();
    subscriptionData.subscription.forEach((subscription) => {
      subscription.subscription_product_entitlements?.forEach((entitlement) => {
        if (entitlement.product_entitlement?.feature) {
          featuresSet.add(entitlement.product_entitlement.feature);
        }
      });
    });

    return Array.from(featuresSet).sort((a, b) => a.localeCompare(b));
  }, [subscriptionData]);

  // Map subscription data with flattened entitlements for easy access
  const subscriptionFeatureFlags = useMemo(() => {
    return subscriptionData?.subscription?.map((subscription) => {
      const rowData: any = {
        id: subscription.id,
        name: subscription.name,
        projects: subscription.projects_owned,
        subscription_product_entitlements:
          subscription.subscription_product_entitlements,
        // Per-feature subscription-level "enable for future projects" values,
        // consumed by the project detail grid's header toggles.
        future_projects_entitlements: subscription.future_projects_entitlements
      };

      // Flatten entitlements into direct properties for grid columns
      subscription.subscription_product_entitlements?.forEach((entitlement) => {
        const feature = entitlement.product_entitlement?.feature;
        if (feature) {
          rowData[feature] = entitlement.enabled;
        }
      });

      return rowData;
    });
  }, [subscriptionData]);

  // Generate dynamic column definitions based on unique features
  const getSubscriptionSearch = useCallback(() => searchTextRef.current, []);

  const handleSearch = useCallback((text: string) => {
    searchTextRef.current = text;
    setSearchText(text);
    const api = gridRef.current?.api;
    if (!api) return;

    api.onFilterChanged();
    // Use the new text (not React state) so clear doesn't expand from a stale
    // value captured before the next render.
    setTimeout(() => {
      if (!gridRef.current?.api) return;
      if (text.trim()) {
        gridRef.current.api.expandAll();
      } else {
        gridRef.current.api.collapseAll();
      }
    }, 100);
  }, []);

  const columnDefs: (ColDef | ColGroupDef)[] = useMemo(() => {
    const dynamicFeatureColumns: ColDef[] = allUniqueFeatures.map(
      (feature) => ({
        field: feature,
        headerName: feature?.replaceAll("_", " "),
        cellRenderer: SubscriptionSwitchCellRenderer,
        headerTooltip: headerTooltipMap[feature] ?? toTitleCase(feature)
      })
    );

    return [
      {
        field: "name",
        headerName: "Subscription",
        width: 320,
        minWidth: 280,
        pinned: "left",
        lockPosition: "left",
        cellRenderer: "agGroupCellRenderer",
        tooltipField: "name",
        getQuickFilterText: (params) => params.value,
        cellRendererParams: {
          suppressCount: true
        },
        headerComponentParams: {
          innerHeaderComponent: ColumnGroupSearchHeader,
          getSearchValue: getSubscriptionSearch,
          onSearchChange: handleSearch,
          placeholder: "Search subscriptions",
          searchAriaLabel: "Search subscriptions"
        }
      },
      {
        headerName: "Subscription Level Features",
        children: dynamicFeatureColumns
      } as ColGroupDef
    ];
  }, [allUniqueFeatures, getSubscriptionSearch, handleSearch]);

  const defaultColDef = useMemo(() => {
    return {
      sortable: false,
      editable: false,
      draggable: false,
      filter: false,
      resizable: true,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isGridReady]);

  return (
    <div className="flex flex-col w-full h-[calc(100vh-136px)] bg-white">
      <CiqLogPageHeader
        {...{
          entityName: "Feature Flag Settings",
          gridRef,
          items: []
        }}
      />
      <div className="grow w-full h-full ag-theme-alpine">
        <AgGridReact
          ref={gridRef}
          tooltipShowMode="standard"
          rowData={subscriptionFeatureFlags}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          headerHeight={42}
          rowHeight={42}
          loading={loading}
          loadingOverlayComponent={GridLoadingIndicator}
          onGridReady={() => {
            setGridReady(true);
            gridRef.current?.api.sizeColumnsToFit();
          }}
          masterDetail
          detailCellRenderer={ProjectDetailCellRenderer}
          detailRowHeight={360}
          isRowMaster={(dataItem) => {
            return dataItem.projects && dataItem.projects.length > 0;
          }}
          context={{
            updateSubscriptionFeatureFlag
          }}
          overlayNoRowsTemplate={
            error
              ? "Error loading feature flag data. Please try again."
              : "No feature flag data available."
          }
          isExternalFilterPresent={() => {
            return !!searchText;
          }}
          doesExternalFilterPass={(rowNode: any) => {
            if (!searchText) return true;

            const searchLower = searchText.toLowerCase().trim();
            const { data } = rowNode;

            if (!data) return false;

            // Subscription search is intentionally limited to subscription names.
            // Project filtering belongs to the nested Project header search.
            const subscriptionName = data.name?.toLowerCase() || "";
            return subscriptionName.includes(searchLower);
          }}
        />
      </div>
    </div>
  );
}

export default FeatureFlagSettings;
